import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';

import Button from '../../core/components/Button.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import TextField from '../../core/components/Fields/TextField.tsx';
import FormField from '../../core/components/FormField.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import Switch from '../../core/components/Switch.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import ExcludedFieldsPicker, { ChannelField } from '../ExcludedFieldsPicker.tsx';

import { FormRow } from './FormsConfig.tsx';
import { CREATE_SELF_SERVICE_FORM, UPDATE_SELF_SERVICE_FORM } from './queries.ts';

interface FormFormData {
    name: string;
    introText: string;
    excludedFields: string[];
    requestsEnabled: boolean;
    autoConfirm: boolean;
}

const DEFAULT_VALUES: FormFormData = {
    name: '',
    introText: '',
    excludedFields: [],
    requestsEnabled: true,
    autoConfirm: false,
};

const ToggleRow = ({
    label,
    helpText,
    value,
    onValueChange,
}: {
    label: string;
    helpText?: string;
    value: boolean;
    onValueChange: (value: boolean) => void;
}) => (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
        <span className="flex flex-col gap-0.5 flex-1 min-w-0">
            <span className="text-sm font-medium text-content-strong">{label}</span>
            {helpText && <span className="text-xs text-content-medium">{helpText}</span>}
        </span>
        <span className="flex-0">
            <Switch value={value} onValueChange={onValueChange} />
        </span>
    </label>
);

interface FormEditorCardProps {
    channelId: string;
    channelFields?: ChannelField[];
    editingForm: FormRow | null;
    onCancel: () => void;
    onSaved: (formId: string) => void;
    onChanged: () => Promise<void>;
}

const FormEditorCard = ({
    channelId,
    channelFields,
    editingForm,
    onCancel,
    onSaved,
    onChanged,
}: FormEditorCardProps) => {
    const { t } = useTranslation('forms');
    const { publishToast } = useToaster();

    const [createForm] = useMutation(CREATE_SELF_SERVICE_FORM);
    const [updateForm] = useMutation(UPDATE_SELF_SERVICE_FORM);

    const form = useForm<FormFormData>({ defaultValues: DEFAULT_VALUES, mode: 'onTouched' });
    const excludedFields = form.watch('excludedFields');
    const requestsEnabled = form.watch('requestsEnabled');

    useEffect(() => {
        if (editingForm) {
            form.reset({
                name: editingForm.name,
                introText: editingForm.introText,
                excludedFields: editingForm.excludedFields ?? [],
                requestsEnabled: editingForm.requestsEnabled,
                autoConfirm: editingForm.autoConfirm,
            });
        } else {
            form.reset(DEFAULT_VALUES);
        }
    }, [editingForm]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (formData: FormFormData) => {
        const variables = {
            name: formData.name.trim(),
            introText: formData.introText.trim(),
            excludedFields: formData.excludedFields,
            requestsEnabled: formData.requestsEnabled,
            autoConfirm: formData.autoConfirm,
        };
        try {
            if (editingForm) {
                await updateForm({ variables: { formId: editingForm.id, ...variables } });
                await onChanged();
                publishToast({ description: t('tenant.forms.toast.updateSuccess'), status: 'success' });
                form.reset(variables);
            } else {
                const result = await createForm({ variables: { channelId, ...variables } });
                await onChanged();
                publishToast({ description: t('tenant.forms.toast.createSuccess'), status: 'success' });
                const createdFormId = result.data?.createSelfServiceForm?.form?.id;
                if (createdFormId) onSaved(createdFormId);
            }
        } catch (e) {
            console.error(e);
            publishToast({
                description: editingForm ? t('tenant.forms.toast.updateError') : t('tenant.forms.toast.createError'),
                status: 'error',
            });
        }
    };

    const toggleField = useCallback(
        (fieldName: string) => {
            const current = form.getValues('excludedFields');
            if (current.includes(fieldName)) {
                form.setValue(
                    'excludedFields',
                    current.filter((n) => n !== fieldName),
                    { shouldDirty: true }
                );
            } else {
                form.setValue('excludedFields', [...current, fieldName], { shouldDirty: true });
            }
        },
        [form]
    );

    return (
        <SettingsFormCard
            onSubmit={form.handleSubmit(handleSubmit)}
            title={editingForm ? t('tenant.forms.edit.title') : t('tenant.forms.create.title')}
            description={t('tenant.forms.description')}
        >
            <Controller
                name="name"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t('tenant.forms.create.name')} helpText={t('tenant.forms.create.nameHelp')}>
                        <StringField {...field} />
                    </FormField>
                )}
            />

            <Controller
                name="introText"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t('tenant.shared.introText')} helpText={t('tenant.shared.introTextHelp')}>
                        <TextField {...field} />
                    </FormField>
                )}
            />

            <ExcludedFieldsPicker
                channelFields={channelFields}
                excludedFields={excludedFields}
                onToggle={toggleField}
            />

            <Controller
                name="requestsEnabled"
                control={form.control}
                render={({ field }) => (
                    <ToggleRow
                        label={t('tenant.forms.create.requestsEnabled')}
                        helpText={t('tenant.forms.create.requestsEnabledHelp')}
                        value={field.value}
                        onValueChange={field.onChange}
                    />
                )}
            />

            {requestsEnabled && (
                <Controller
                    name="autoConfirm"
                    control={form.control}
                    render={({ field }) => (
                        <ToggleRow
                            label={t('tenant.forms.create.autoConfirm')}
                            helpText={t('tenant.forms.create.autoConfirmHelp')}
                            value={field.value}
                            onValueChange={field.onChange}
                        />
                    )}
                />
            )}

            {editingForm ? (
                <SaveStatus
                    isDirty={form.formState.isDirty}
                    isSubmitting={form.formState.isSubmitting}
                    errorCount={countFieldErrors(form.formState.errors)}
                >
                    <Button type="button" variant="outline" onClick={onCancel}>
                        {t('tenant.shared.cancel')}
                    </Button>
                </SaveStatus>
            ) : (
                <div className="flex justify-end">
                    <Button type="submit" variant="primary">
                        {t('tenant.forms.create.submit')}
                    </Button>
                </div>
            )}
        </SettingsFormCard>
    );
};

export default FormEditorCard;
